import {
	Badge,
	Card,
	Checkbox,
	ConsentText,
	FlowPage,
	Link,
	PageHeader,
} from '@monthio/design-system'
import { useIsMobile } from '#modules/Bank/useBankHooks'
import { useBankModule } from '#modules/Bank/useBankModule'
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { getIcon } from '../CardConsent/consentIcons'
import { parseConsent } from '../CardConsent/consentParser'
import { TextWithLinks } from '../TextWithLinks/TextWithLinks'

interface TranslatedConsent {
	header: string
	id: string
	isOptional: boolean
	requiredText: string
	text: string
}
interface MonthioConsentProps {
	consent: TranslatedConsent
	onAcceptChange: (id: string, isAccepted: boolean) => void
}

export const MonthioConsent = ({
	consent,
	onAcceptChange,
}: MonthioConsentProps) => {
	const [isAccepted, setIsAccepted] = useState<boolean>(false)
	const { config } = useBankModule()
	const headerId = `monthio-consent-header-${consent.id}`
	const textId = `monthio-consent-text-${consent.id}`
	const { t } = useTranslation()
	const cardItems = useMemo(() => parseConsent(consent.text), [consent.text])

	const isMobile = useIsMobile()

	const handleClick = useCallback(() => {
		onAcceptChange(consent.id, isAccepted)
	}, [isAccepted, onAcceptChange, consent.id])

	const handleAcceptChange = useCallback((isChecked: boolean) => {
		setIsAccepted(isChecked)
	}, [])

	const flowContent =
		cardItems != null ? (
			<FlowPage.Content $spacing="xl">
				<PageHeader
					id={headerId}
					title={consent.header}
					description={t('monthio-consent-explanation')}
				/>

				<Card id={textId}>
					<Card.Section>
						{cardItems.map((content, i) => (
							<Card.Content key={i}>
								<Badge type="grey" size="l">
									{getIcon(content.iconName)}
								</Badge>
								{content.text}
							</Card.Content>
						))}
					</Card.Section>
					{isMobile ? null : (
						<Card.Section>
							<Checkbox
								aria-describedby={textId}
								aria-required="true"
								isSelected={isAccepted}
								onChange={handleAcceptChange}
							>
								{t('monthioConsent-label')}
							</Checkbox>
						</Card.Section>
					)}
				</Card>
				<FlowPage.FinePrint>
					<Trans
						i18nKey="monthio-consent-withdrawal-note"
						t={t}
						components={{
							CustomLink: <Link />,
						}}
						values={{ clientName: config?.clientName ?? '' }}
					/>
				</FlowPage.FinePrint>
			</FlowPage.Content>
		) : (
			<FlowPage.Content $spacing="xs">
				<FlowPage.Header id={headerId} title={consent.header} />
				<ConsentText id={textId}>
					<TextWithLinks rawText={consent.text} />
				</ConsentText>
			</FlowPage.Content>
		)

	return (
		<FlowPage>
			{flowContent}
			<FlowPage.Footer
				actions={
					<FlowPage.Cta
						aria-label={
							isAccepted
								? t('monthioConsent-continueBtn-ariaLabel')
								: t('monthioConsent-continueBtn-noConsent-ariaLabel')
						}
						disabled={!isAccepted}
						onClick={handleClick}
					>
						{t('btn-next')}
					</FlowPage.Cta>
				}
				additionalContent={
					cardItems !== null && !isMobile ? null : (
						<ConsentContainer>
							<Checkbox
								aria-describedby={textId}
								aria-required="true"
								isSelected={isAccepted}
								onChange={handleAcceptChange}
							>
								{t('monthioConsent-label')}
							</Checkbox>
						</ConsentContainer>
					)
				}
			/>
		</FlowPage>
	)
}

const ConsentContainer = styled.div`
	display: flex;
	align-items: center;
`
